import Vue from "vue";
import { FormOptions, AudioAccent, AudioGender, AudioSpeed } from "@tt-app/models";

import IconAustralia from "@joyz/app-core/assets/flags/Australia.svg";
import IconUnitedKingdom from "@joyz/app-core/assets/flags/UnitedKingdom.svg";
import IconUnitedStates from "@joyz/app-core/assets/flags/UnitedStates.svg";

export const ACCENT_US = 0;
export const ACCENT_UK = 1;

const flags: { [n: number]: string } = {
  0: IconUnitedStates,
  1: IconUnitedKingdom,
  2: IconAustralia
};

export const GET_ACCENT_OPTIONS = (comp: Vue): FormOptions<number>[] => {
  return [ACCENT_US, ACCENT_UK].map(n => ({
    value: n,
    icon: flags[n],
    label: comp.$__(`user.settings.accent.${n}`),
    description: comp.$__(`user.settings.accent.${n}.description`)
  }));
};

export const GET_SPEED_OPTIONS = (comp: Vue): FormOptions<number>[] => {
  return [50, 75, 90, 100, 110, 125].map(n => ({
    value: n,
    label: comp.$__(`user.settings.speed.${n}`),
    description: comp.$__(`user.settings.speed.${n}.description`)
  }));
};

export const GET_STT_CONNECTION_METHOD_OPTIONS = (
  comp: Vue,
  recommended?: number
): FormOptions<boolean>[] => {
  return [false, true].map((b, index) => {
    let label = comp.$__(`user.settings.stt-connection-method.${b}`);

    if (index === recommended) {
      label = `${label} ${comp.$__("common.recommended")}`;
    }

    return {
      value: b,
      label,
      description: comp.$__(`user.settings.stt-connection-method.${b}.description`)
    };
  });
};

const AUDIO_CONFIRM_BASE_URL = process.env.VUE_APP_AUDIO_CONFIRMATION_BASE_URL;

/**
 * List of audio files saying "Prepare for your lessons" to auto-play when
 * the user selects an accent or a speech speed as a confirmation / sample
 *
 * Taken from https://github.com/joyz-inc/tt-android/blob/13d224323d9c06173188a0805964980dff983394/app/src/main/java/jp/co/joyz/terratalk/r/model/PrepareForYourLessonAudio.java
 * Monday.com link: https://joyz.monday.com/boards/527178971/pulses/596834681
 */
export const AUDIO_CONFIRMATIONS_URL: {
  [key in AudioAccent]: {
    [key in AudioGender]: {
      [key in AudioSpeed]: string;
    };
  };
} = {
  0: {
    F: {
      50: `${AUDIO_CONFIRM_BASE_URL}/v1/ff76dbcc2f627bdc80fa1416f51c60bd.mp3`,
      75: `${AUDIO_CONFIRM_BASE_URL}/v1/3ec6d98650063a00f321ebbd51147e02.mp3`,
      90: `${AUDIO_CONFIRM_BASE_URL}/v1/d5bf5d5e717925d5809317aacb72ed13.mp3`,
      100: `${AUDIO_CONFIRM_BASE_URL}/v1/8fa72991d4f206a50f897e986a1eebef.mp3`,
      110: `${AUDIO_CONFIRM_BASE_URL}/v1/150bbcbc8cff29130bac97a3b9956ee0.mp3`,
      125: `${AUDIO_CONFIRM_BASE_URL}/v1/fb8d1244be1b7814e7856c8a51f0160b.mp3`
    },
    M: {
      50: `${AUDIO_CONFIRM_BASE_URL}/v1/f6ced039331c6b764d227f85f946ac03.mp3`,
      75: `${AUDIO_CONFIRM_BASE_URL}/v1/4cf579737e063c823841b1208755104d.mp3`,
      90: `${AUDIO_CONFIRM_BASE_URL}/v1/b2383fa603cb96930da288afe7f23d08.mp3`,
      100: `${AUDIO_CONFIRM_BASE_URL}/v1/3d544e4cd3bf9c904e55fd4aec3ca784.mp3`,
      110: `${AUDIO_CONFIRM_BASE_URL}/v1/93b07bac6e62f5287abdd53b341fa27c.mp3`,
      125: `${AUDIO_CONFIRM_BASE_URL}/v1/a2adaf4fe8547641485431a528b17cf1.mp3`
    }
  },
  1: {
    F: {
      50: `${AUDIO_CONFIRM_BASE_URL}/v1/ba8ef13b902dd4b636fa08e19d5f7746.mp3`,
      75: `${AUDIO_CONFIRM_BASE_URL}/v1/4bb4b8e1d6d1b98334f06684311d1293.mp3`,
      90: `${AUDIO_CONFIRM_BASE_URL}/v1/4332c7e09e3535f6bb65f87c604a9713.mp3`,
      100: `${AUDIO_CONFIRM_BASE_URL}/v1/e1db265eacf3b13196d6ed782ef37a9b.mp3`,
      110: `${AUDIO_CONFIRM_BASE_URL}/v1/ee87c40ab121540e4cb0f0a3734edceb.mp3`,
      125: `${AUDIO_CONFIRM_BASE_URL}/v1/4439dc321ef041d4b5aa1e66c77b0020.mp3`
    },
    M: {
      50: `${AUDIO_CONFIRM_BASE_URL}/v1/e7126427db9c9b425d5fefcb4e674a52.mp3`,
      75: `${AUDIO_CONFIRM_BASE_URL}/v1/21cd2ceed5fc96b5537e3ba060cf37e2.mp3`,
      90: `${AUDIO_CONFIRM_BASE_URL}/v1/17aef8c3d4aeda99cb7f73b9ccb0e576.mp3`,
      100: `${AUDIO_CONFIRM_BASE_URL}/v1/9567412e00eb9269c996949cf12eeb5c.mp3`,
      110: `${AUDIO_CONFIRM_BASE_URL}/v1/2d5d76c959ca778587cc456e76ccb880.mp3`,
      125: `${AUDIO_CONFIRM_BASE_URL}/v1/3581797b1591c9e736054986f3a61cee.mp3`
    }
  },
  2: {
    F: {
      50: `${AUDIO_CONFIRM_BASE_URL}/v1/16fd197b4c503d8bea7833a4e32ca7cb.mp3`,
      75: `${AUDIO_CONFIRM_BASE_URL}/v1/b50ecc43ae3080ad0e28619710f10aeb.mp3`,
      90: `${AUDIO_CONFIRM_BASE_URL}/v1/147894fc16466a99661c99fd7dd4b816.mp3`,
      100: `${AUDIO_CONFIRM_BASE_URL}/v1/75faaa05e79edfad50a479bc39b7f383.mp3`,
      110: `${AUDIO_CONFIRM_BASE_URL}/v1/f678e09e7029db5345eb3c681ce2180c.mp3`,
      125: `${AUDIO_CONFIRM_BASE_URL}/v1/adc8d5298f59361a4af919384e50c1fe.mp3`
    },
    M: {
      50: `${AUDIO_CONFIRM_BASE_URL}/v1/565c24ac15f5270ebde35f083fb2057e.mp3`,
      75: `${AUDIO_CONFIRM_BASE_URL}/v1/51763805c6e463624ed4e681644fb8db.mp3`,
      90: `${AUDIO_CONFIRM_BASE_URL}/v1/3143818f79ef249d71ce38cdc9d41fe0.mp3`,
      100: `${AUDIO_CONFIRM_BASE_URL}/v1/55ee6f1a213beefd8f04de8dd7a42876.mp3`,
      110: `${AUDIO_CONFIRM_BASE_URL}/v1/363e398add1d632b796ffd49e2a6adcf.mp3`,
      125: `${AUDIO_CONFIRM_BASE_URL}/v1/07672ff78caf8ee5622e83935e6e518e.mp3`
    }
  }
};

export const HELP_GUIDE_LINK = "https://joyz.quip.com/utoTAgnbzVx7";
